import { useContext, useEffect, useId } from 'react';

import { GadgetStatusContext } from 'contextProviders/GadgetStatusBoundary';
import retryOnce from 'utils/retryOnce';

const DELAY = process.env.NODE_ENV === 'test' ? 0 : 300;

/**
 * An effect for managing asynchronous side effects
 * It will ensure:
 * - The callback will be debounced
 * - No race conditions will occur
 *
 * - A rejected callback reaches errorHandler (when given) instead of being
 *   left unhandled, so callers can leave their loading state
 * - Inside a gadget (GadgetStatusBoundary), each request reports its state:
 *   a failure the caller doesn't handle shows the gadget's error, and a
 *   scheduled render waits for every request to finish
 * - With shouldRetryOnce, a failed request is tried once more before it
 *   counts as failed. Only for reads: a retried write could apply twice
 *
 * This is useful for networking which dependent on many sources
 * of filtering, which will be built over the course of a number of renders
 */
const useLockedDebouncedEffect = <ArgsType, ResponseType>({
  args,
  callback,
  responseHandler,
  errorHandler,
  shouldRetryOnce = false,
}: {
  args: ArgsType | undefined;
  callback: (args: ArgsType) => Promise<ResponseType>;
  responseHandler: (response: ResponseType) => void;
  errorHandler?: (error: unknown) => void;
  shouldRetryOnce?: boolean;
}) => {
  const { reportRequest } = useContext(GadgetStatusContext);
  const requestId = useId();

  useEffect(() => {
    if (args === undefined) {
      reportRequest(requestId, undefined);
      return;
    }

    let isActive = true;
    reportRequest(requestId, 'loading');
    const timeoutId = setTimeout(async () => {
      if (!isActive) {
        return;
      }
      let response: ResponseType;
      try {
        response = shouldRetryOnce
          ? await retryOnce({ run: () => callback(args) })
          : await callback(args);
      } catch (error) {
        if (!isActive) {
          return;
        }
        if (errorHandler) {
          reportRequest(requestId, 'failed-handled');
          errorHandler(error);
        } else {
          console.warn(error);
          reportRequest(requestId, 'failed');
        }
        return;
      }
      if (!isActive) {
        return;
      }
      reportRequest(requestId, 'done');
      responseHandler(response);
    }, DELAY);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [
    args,
    callback,
    responseHandler,
    errorHandler,
    shouldRetryOnce,
    reportRequest,
    requestId,
  ]);

  useEffect(
    () => () => reportRequest(requestId, undefined),
    [reportRequest, requestId],
  );
};

export default useLockedDebouncedEffect;
